// src/components/course/WordCard.js
import React from 'react';
import clsx from 'clsx'; // Using clsx for cleaner class management
import { useTranslation } from '../../hooks/useTranslation';
import { srsMapping } from '../../utils/mappings';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import GlassyBadge from '../common/GlassyBadge';

const WordCard = ({ word, isSelectMode, isSelected, onSelectWord, onCardClick, selectionDisabled, learnedIds }) => {
  const [showTranslation] = useTranslation();

  // --- Card Data and Logic ---
  // Prefer live learnedIds set (from MetadataProvider) over stored item field to avoid stale state
  const isLearned = learnedIds ? learnedIds.has(word.id) : false;
  const isSkipped = word.skipped || false;
  const srsStage = isLearned ? (isSkipped ? 7 : word.srs_stage ?? 0) : 0;
  const nLevel = word.level ?? 0;
  const translationText = word.senses?.[0]?.translation?.[0] || '';
  const isDisabled = isSelectMode && (isLearned || (selectionDisabled && !isSelected));

  // --- Event Handlers ---
  const handleCardClick = () => {
    if (isSelectMode) {
      if (!isLearned && !selectionDisabled) {
        onSelectWord(word.id);
      }
    } else {
      onCardClick();
    }
  };

  // --- Dynamic styles ---
  // MODIFIED: This function now only handles styles that CANNOT be in the CSS component layer
  const getDynamicStyles = () => {
    if (isLearned) {
      return {
        borderTopColor: `var(--color-srs-${srsStage})`,
        boxShadow: `0 4px 16px var(--color-srs-${srsStage})30`,
      };
    }
    return {
      borderTopColor: 'var(--color-border)', // border color
    };
  };

  const cardStyles = getDynamicStyles();

  // --- Card Classes ---
  // MODIFIED: Replaced hardcoded hover effects with your CSS component classes
  const cardClasses = clsx(
    'group',
    'relative',
    'flex',
    'flex-col',
    'justify-between',
    'p-4',
    'rounded-xl',
    'backdrop-blur-sm',
    'border-2',
    'border-border',
    'border-t-4',
    'overflow-hidden',
    !isSelectMode && !isDisabled && 'hover:shadow-lg', // Keep card-specific shadow
    isSelected && 'ring-2 ring-primary ring-offset-2 ring-offset-background-primary',
    isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
    {
      'item-unlearned item-unlearned-hover': !isLearned,
      'item-learned item-learned-hover': isLearned,
    }
  );

  return (
    <div onClick={handleCardClick} className={cardClasses} style={cardStyles}>
      {/* Glassmorphic overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

      {/* --- SELECTION UI --- */}
      {isSelectMode && !isLearned && (
        <div className="absolute top-3 right-3 z-20 h-6 w-6">
          <div
            className={`h-full w-full rounded-full flex items-center justify-center border-2 transition-all duration-200 backdrop-blur-sm ${
              isSelected
                ? 'bg-primary border-primary shadow-lg'
                : 'bg-background-secondary/80 border-content-secondary/40 dark:bg-background-secondary/60'
            }`}
          >
            {isSelected && <CheckCircleIcon className="h-4 w-4 text-white drop-shadow-sm" />}
          </div>
        </div>
      )}

      {/* --- Main Content --- */}
      <div className="relative z-10 flex flex-col items-center justify-center flex-grow w-full py-2 overflow-hidden">
        <h3
          className={`text-3xl font-bold break-words my-auto truncate w-full text-center drop-shadow-sm transition-colors duration-75 ${
            isSelected ? 'text-primary' : !isDisabled ? 'text-content-primary group-hover:text-primary' : 'text-content-primary'
          }`}
          lang="ja"
        >
          {word.word}
        </h3>

        {/* Translation with sliding animation */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out w-full ${showTranslation ? 'max-h-8 mt-1' : 'max-h-0 mt-0'}`}>
          <p
            className={`text-base truncate w-full text-center drop-shadow-sm text-content-secondary opacity-80 ${
              !isDisabled ? 'transition-colors duration-75 group-hover:text-content-primary group-hover:opacity-100' : ''
            }`}
          >
            {translationText}
          </p>
        </div>
      </div>

      {/* --- FOOTER --- */}
      <div className="relative z-10 flex justify-center items-center pt-3 border-t border-border min-h-[34px] gap-2">
        {/* N-Level Badge */}
        <GlassyBadge level={nLevel} size="md" variant="solid">
          N{nLevel}
        </GlassyBadge>

        {/* SRS Stage Badge */}
        {isLearned && (
          <GlassyBadge srsStage={srsStage} size="md" variant="solid">
            {srsMapping[srsStage]}
          </GlassyBadge>
        )}
      </div>
    </div>
  );
};

export default WordCard;
